import type { Party, Usufruct } from 'generated/gql';
import { Block, Box, Text } from '@rulebooks/evidend-titan';
import { Trans } from '@lingui/macro';
import type { SummaryCardsComponentProps } from 'helpers/formatMutation';
import { useLabels } from 'hooks/useLabels';
import { uniqBy } from 'lodash';
import React, { useMemo } from 'react';
import { CapitalCardListItem } from 'summaries/CapitalCards/CapitalCardListItem';
import { ShareholderCapitalCard } from 'summaries/CapitalCards/ShareholderCapitalCard';
import { DeedCard } from 'summaries/DeedCard/DeedCard';
import DelegateRightsCard from 'summaries/DelegateRightsCard/DelegateRightsCard';
import PartyCard from 'summaries/PartyCard/PartyCard';

const getUsufructsOfParty = (usufructs: Usufruct[], party: Party): Usufruct[] =>
  usufructs.filter((usufruct) => usufruct.owner.id === party.id);

export const UsufructSummaryCards = ({
  backgroundVariant = 'white',
  mutation,
  onDocumentSave,
}: SummaryCardsComponentProps) => {
  const { register } = mutation;
  const { legalEntity } = register;
  const labels = useLabels('usufruct.summary', legalEntity.isFoundation);
  const usufructs = useMemo(() => mutation.usufruct || [], [mutation.usufruct]);
  const parties = useMemo(
    () =>
      uniqBy(
        usufructs.map((usufruct) => usufruct.owner),
        'id',
      ),
    [usufructs],
  );

  const delegatee = useMemo(() => usufructs[0]?.details?.delegatee, [usufructs]);
  const rights = useMemo(() => usufructs[0]?.details, [usufructs]);

  return (
    <>
      {!!parties.length && (
        <>
          <Text color="grey80" spaceAfter={6} type="labelBold">
            {labels.shares}
          </Text>

          <Box spaceAfter={10}>
            {parties.map((party) => (
              <ShareholderCapitalCard
                key={party.id}
                backgroundVariant={backgroundVariant}
                compareDeceaseDate={mutation.effectiveDate}
                party={party}
              >
                {getUsufructsOfParty(usufructs, party).map((usufruct) => (
                  <CapitalCardListItem
                    key={usufruct.shareSeries.shareType.id}
                    mutationOptions={{ showPaidStatus: true }}
                    shareSeries={usufruct.shareSeries}
                  />
                ))}
              </ShareholderCapitalCard>
            ))}
          </Box>
        </>
      )}

      {!!delegatee && (
        <>
          <Text color="grey80" spaceAfter={6} type="labelBold">
            <Trans>Vruchtgebruiker</Trans>
          </Text>

          <PartyCard
            backgroundVariant={backgroundVariant}
            compareDeceaseDate={mutation.effectiveDate}
            party={delegatee}
          />
        </>
      )}

      {!!rights && (
        <>
          <Text color="grey80" spaceAfter={6} type="labelBold">
            <Trans>Rechten vruchtgebruiker</Trans>
          </Text>

          <DelegateRightsCard backgroundVariant={backgroundVariant} rights={rights} />
        </>
      )}

      {mutation.document && (
        <Block spaceAfter={10}>
          <DeedCard
            backgroundVariant={backgroundVariant}
            document={mutation.document}
            mutation={mutation}
            onDocumentSave={onDocumentSave}
          />
        </Block>
      )}
    </>
  );
};
